import { api, ApiError, unwrap, type Schemas } from '../../api/client';
import { useApiQuery } from '../../api/hooks';
import { isSyncing } from './feedLib';

// Bank feeds (ADR 0020, apps/api/src/bankFeeds.ts). Queries sit under the 'bank' key, so the
// invalidations Banking already makes refresh them too.

/** The generated schema leaves every field optional; these the API always returns. */
export type FeedConnection = Schemas['BankFeedConnection'] & {
  id: string;
  label: string;
  profile_id: string;
  time_zone: string;
  created_at: string;
  status: 'active' | 'needs_attention' | 'disconnected';
};
export type FeedBalance = Schemas['BankFeedBalance'] & { id: string; currency: string; supported: boolean };
export type FeedAccount = NonNullable<FeedConnection['accounts']>[number];
export type FeedPreview = {
  lines: number;
  new_lines: number;
  look_like_existing: number;
  first_date: string | null;
  last_date: string | null;
  default_start_date: string | null;
};
export type FeedEvent = {
  id: string;
  kind: 'connected' | 'token_replaced' | 'balance_linked' | 'balance_unlinked' | 'disconnected' | 'sync_requested' | 'sync_finished' | 'credential_read' | 'credential_resealed' | 'attention_needed';
  actor: 'member' | 'cron' | 'sync';
  user_name: string | null;
  detail: Record<string, unknown>;
  created_at: string;
};

async function dataOf<T>(call: Promise<{ data?: unknown; error?: unknown; response: Response }>): Promise<T> {
  return ((await unwrap(call)) as unknown as { data: T }).data;
}

const path = (id: string) => ({ params: { path: { id } } });

export const getFeeds = () => dataOf<FeedConnection[]>(api.GET('/v1/bank-feeds'));
export const connectWise = (body: { token: string; label?: string; time_zone: string }) =>
  dataOf<{ connection: FeedConnection; balances: FeedBalance[] }>(api.POST('/v1/bank-feeds/wise', { body }));
export const getFeedBalances = (id: string) => dataOf<FeedBalance[]>(api.GET('/v1/bank-feeds/{id}/balances', path(id)));
export type LinkInput = { balance_id: string; account_id: string; start_date: string };
export const previewFeed = (id: string, body: LinkInput) =>
  dataOf<FeedPreview>(api.POST('/v1/bank-feeds/{id}/accounts', { ...path(id), body: { ...body, dry_run: true } }));
export const linkFeed = (id: string, body: LinkInput) => dataOf<FeedConnection>(api.POST('/v1/bank-feeds/{id}/accounts', { ...path(id), body }));
export const syncFeed = (id: string) => dataOf<{ queued: boolean }>(api.POST('/v1/bank-feeds/{id}/sync', path(id)));
export const replaceFeedToken = (id: string, token: string) =>
  dataOf<FeedConnection>(api.POST('/v1/bank-feeds/{id}/token', { ...path(id), body: { token } }));
export const disconnectFeed = (id: string) => dataOf<FeedConnection>(api.POST('/v1/bank-feeds/{id}/disconnect', path(id)));
export const getFeedEvents = (id: string) => dataOf<FeedEvent[]>(api.GET('/v1/bank-feeds/{id}/events', path(id)));

/** The environment has no bank feed set up: hide the feature rather than show an error. */
export const feedsOff = (error: unknown) => error instanceof ApiError && error.code === 'bank_feed_not_configured';

/**
 * The connections. While a sync is running in the background, poll every few seconds so the cards
 * and lines show its result without a reload.
 */
export const useFeeds = () =>
  useApiQuery(['bank', 'feeds'], getFeeds, {
    retry: (count, error) => !feedsOff(error) && count < 2,
    refetchInterval: (query) => (query.state.data?.some((c) => isSyncing(c)) ? 3000 : false),
  });

export const useFeedBalances = (id: string | null, enabled = true) =>
  useApiQuery(['bank', 'feed-balances', id], () => getFeedBalances(id as string), { enabled: !!id && enabled, staleTime: 30_000 });

export const useFeedEvents = (id: string | null, enabled = true) =>
  useApiQuery(['bank', 'feed-events', id], () => getFeedEvents(id as string), { enabled: !!id && enabled });
